import { Stack } from '@koob/margaret';
import { useTranslation } from 'react-i18next';
import PillStatus from './PillStatus';
import PromotionBenefit from './PromotionBenefit';
import { useTheme } from 'styled-components';
import {
  getPromotionAppliesTo,
  getPromotionStatusBadges,
  PromotionAppliesToEnum,
} from 'utils/promotions';
import {
  KoobBadge,
  KoobDataTable,
  KoobTableAction,
  koobTableUrlSort,
} from '@koob/ui';
import { useSearchParams } from 'hooks';

const PromotionsListTable = ({
  data = [],
  headers,
  isReadOnly = false,
  loading = false,
  pagination,
}) => {
  const { t } = useTranslation(['promotions', 'promotionForm']);
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort, onSortChange } = koobTableUrlSort(
    searchParams,
    setSearchParams,
  );

  const DealsMap = new Map([
    [
      'basic_deal',
      <Stack gutterSize={0.5} as="span" alignY="center" key={0}>
        <i
          className="fa-regular fa-tag"
          style={{ fontSize: 18, color: theme.textLighter }}
        />
        <span>{t(['deal.basic_deal'])}</span>
      </Stack>,
    ],
    [
      'early_booker',
      <Stack gutterSize={0.5} as="span" alignY="center" key={1}>
        <i
          className="fa-regular fa-alarm-clock"
          style={{ fontSize: 18, color: theme.textLighter }}
        />
        <span>{t(['deal.early_booker'])}</span>
      </Stack>,
    ],
    [
      'last_minute',
      <Stack gutterSize={0.5} as="span" alignY="center" key={2}>
        <i
          className="fa-regular fa-stopwatch"
          style={{ fontSize: 18, color: theme.textLighter }}
        />
        <span>{t(['deal.last_minute'])}</span>
      </Stack>,
    ],
    [
      'stay_flexibility',
      <Stack gutterSize={0.5} as="span" alignY="center" key={3}>
        <i
          className="fa-regular fa-calendar"
          style={{ fontSize: 18, color: theme.textLighter }}
        />
        <span>{t(['deal.stay_flexibility'])}</span>
      </Stack>,
    ],
  ]);

  const cellRenderers = {
    kind: (node) =>
      DealsMap.has(node?.kind) ? DealsMap.get(node?.kind) : 'N/A',
    name: (node) => node?.name || 'N/A',
    minNumberOfRooms: (node) => node?.minNumberOfRooms || 'N/A',
    benefit: (node) => <PromotionBenefit promotion={node} />,
    status: (node) => {
      const badges = getPromotionStatusBadges(node ?? {});
      if (badges.length === 0) return '-';
      return (
        <Stack gutterSize={0.25} as="span" alignY="center">
          {badges.map((badge) => (
            <PillStatus key={badge} value={badge} emptyValue="-" />
          ))}
        </Stack>
      );
    },
    appliesTo: (node) => {
      const appliesTo = getPromotionAppliesTo(node);
      return (
        <KoobBadge
          color={appliesTo === PromotionAppliesToEnum.NOT_SET ? 'red' : 'blue'}
        >
          {t(`appliesTo.${appliesTo}`)}
        </KoobBadge>
      );
    },
    tos: (node) => (
      <KoobBadge
        color={
          node?.appliedToCount > 0
            ? 'green'
            : node?.appliesToDmc
              ? 'gray'
              : 'red'
        }
      >
        {node?.appliedToCount || 0}
      </KoobBadge>
    ),
  };

  const actions = (node) =>
    node?.id ? (
      <>
        <KoobTableAction
          icon={<i className="fa-light fa-chevron-right" aria-hidden="true" />}
          label={t('checkoutPromotion')}
          to={node.id}
        />
        {!isReadOnly && (
          <KoobTableAction
            icon={<i className="fa-regular fa-copy" aria-hidden="true" />}
            label={t('duplicate')}
            to={`./duplicate/${node.id}`}
          />
        )}
      </>
    ) : null;

  const columns = (headers ?? [])
    .filter((header) => header.key !== 'action')
    .map((header) => ({
      ...header,
      render: cellRenderers[header.key],
    }));

  return (
    <KoobDataTable
      columns={columns}
      data={data ?? []}
      loading={loading}
      actions={actions}
      minWidth={900}
      sort={sort}
      onSortChange={onSortChange}
      pagination={pagination}
    />
  );
};

export default PromotionsListTable;
